// Utility functions for generating legends & statistics for the map

// generate general statistics, for use in the legend and for debugging
function generalStatistics(valuesUnordered) {
  const valuesOrdered = valuesUnordered.sort((a, b) => a - b);
  const minValues = valuesOrdered[0];
  const maxValues = valuesOrdered[valuesOrdered.length - 1];
  const meanValues =
    valuesOrdered.reduce((a, b) => a + b, 0) / valuesOrdered.length;
  const sdValues = Math.sqrt(
    valuesOrdered
      .map((v) =>
        Math.pow(
          v - valuesOrdered.reduce((a, b) => a + b, 0) / valuesOrdered.length,
          2
        )
      )
      .reduce((a, b) => a + b, 0) / valuesOrdered.length
  );
  const medianValues = valuesOrdered[Math.floor(valuesOrdered.length / 2)];
  const Q1Values = valuesOrdered[Math.floor(valuesOrdered.length / 4)];
  const Q3Values = valuesOrdered[Math.floor((valuesOrdered.length * 3) / 4)];
  const uniqueValues = Array.from(new Set(valuesOrdered)).length;
  const wholeNumberValues = valuesOrdered.every((v) => {
    const num = Number(v);
    return !isNaN(num) && num === Math.floor(num);
  });
  const maxCharactersValues = Math.max(
    ...valuesOrdered.map((c) => String(c).length)
  );

  return {
    min: minValues,
    max: maxValues,
    range: maxValues - minValues,

    mean: meanValues,
    sd: sdValues,
    lowerSd: meanValues - sdValues,
    upperSd: meanValues + sdValues,

    Q1: Q1Values,
    median: medianValues,
    Q3: Q3Values,

    wholeNumber: wholeNumberValues,
    maxCharacters: maxCharactersValues,
    uniqueValues: uniqueValues,
  };
}

// Helper function to calculate equal interval breaks
function equalIntervalBreaks(values, intervals = 5) {
  const minValues = Math.min(...values);
  const maxValues = Math.max(...values);
  const interval = (maxValues - minValues) / intervals;
  let breaks = [];
  for (let i = 0; i <= intervals - 1; i++) {
    breaks.push(minValues + i * interval);
  }
  breaks.push(maxValues); // ensure max value is included
  return breaks;
}

// Helper function to calculate equal count breaks (quantiles)
// 4 intervals by default creates 5 break points (quartiles)
function equalCountBreaks(values, intervals = 4) {
  const sortedValues = [...values].sort((a, b) => a - b);
  const n = sortedValues.length;

  if (n === 0) return [];
  if (n === 1) return [sortedValues[0]];

  const breaks = [];

  // Always include the minimum
  breaks.push(sortedValues[0]);

  // Calculate quantile breaks
  for (let i = 1; i < intervals; i++) {
    const position = (i * n) / intervals;
    const lowerIndex = Math.floor(position);
    const upperIndex = Math.ceil(position);

    // If position is exactly on an index, use that value
    if (lowerIndex === upperIndex || upperIndex >= n) {
      const index = Math.min(lowerIndex, n - 1);
      breaks.push(sortedValues[index]);
    } else {
      // Interpolate between two values
      const fraction = position - lowerIndex;
      const interpolated =
        sortedValues[lowerIndex] +
        fraction * (sortedValues[upperIndex] - sortedValues[lowerIndex]);
      breaks.push(interpolated);
    }
  }

  // Always include the maximum
  breaks.push(sortedValues[n - 1]);

  // Remove duplicates while preserving order
  const uniqueBreaks = breaks.filter(
    (value, index, self) => index === 0 || value !== self[index - 1]
  );

  return uniqueBreaks;
}

function jenksNaturalBreaks(data, numClasses) {
  // Sort data and remove duplicates
  const sorted = [...new Set(data)].sort((a, b) => a - b);
  const n = sorted.length;

  // If we have fewer unique values than classes, return the sorted values
  if (n <= numClasses) {
    return sorted;
  }

  // Initialize matrices for dynamic programming
  const mat1 = Array(n + 1)
    .fill(null)
    .map(() => Array(numClasses + 1).fill(0));
  const mat2 = Array(n + 1)
    .fill(null)
    .map(() => Array(numClasses + 1).fill(0));

  // Initialize first row and column
  for (let i = 1; i <= numClasses; i++) {
    mat1[1][i] = 1;
    mat2[1][i] = 0;
    for (let j = 2; j <= n; j++) {
      mat2[j][i] = Infinity;
    }
  }

  // Calculate variance for a range of values
  const variance = (start, end) => {
    let sum = 0;
    let sumSquares = 0;
    const count = end - start + 1;

    for (let i = start; i <= end; i++) {
      sum += sorted[i - 1];
      sumSquares += sorted[i - 1] ** 2;
    }

    return sumSquares - sum ** 2 / count;
  };

  // Dynamic programming to find optimal class breaks
  for (let l = 2; l <= n; l++) {
    let sum = 0;
    let sumSquares = 0;
    let w = 0;

    for (let m = 1; m <= l; m++) {
      const lowerClassLimit = l - m + 1;
      const val = sorted[lowerClassLimit - 1];

      w++;
      sum += val;
      sumSquares += val ** 2;

      const currentVariance = sumSquares - sum ** 2 / w;
      const i4 = lowerClassLimit - 1;

      if (i4 !== 0) {
        for (let j = 2; j <= numClasses; j++) {
          if (mat2[l][j] >= currentVariance + mat2[i4][j - 1]) {
            mat1[l][j] = lowerClassLimit;
            mat2[l][j] = currentVariance + mat2[i4][j - 1];
          }
        }
      }
    }

    mat1[l][1] = 1;
    mat2[l][1] = variance(1, l);
  }

  // Extract the break points
  const breaks = [];
  let k = n;

  for (let j = numClasses; j >= 1; j--) {
    const id = mat1[k][j] - 1;
    if (id >= 0) {
      breaks.unshift(sorted[id]);
    }
    k = mat1[k][j] - 1;
  }
  breaks.push(sorted[sorted.length - 1]); // ensure max value is included
  // Remove duplicates and sort the breaks
  const uniqueBreaks = Array.from(new Set(breaks)).sort((a, b) => a - b);
  return uniqueBreaks;
}

const breaks = {
  jenksNatural: jenksNaturalBreaks,
  equalInterval: equalIntervalBreaks,
  equalCount: equalCountBreaks,
};

export { generalStatistics, breaks };
